import { cn } from '@/lib/cn'

/**
 * The crest.
 *
 * 'De Leeuw' means 'the lion', and the heraldic engraving is the mark the
 * business is built around. It is an engraving — thousands of fine strokes —
 * so it is not redrawn as SVG paths; it is rendered as a CSS mask over
 * `currentColor`, which means one file serves every size and inverts to cream
 * on an ink surface without a second asset.
 *
 * See scripts/prep-studio-brand.mjs for how the masks are derived from the
 * source artwork.
 */

const MASK_BASE = 'shrink-0 bg-current [mask-position:center] [mask-repeat:no-repeat] [mask-size:contain]'

/** Head and mane in profile. The only crop that still reads below about 32px. */
export function Lion({ size = 24, className }: { size?: number; className?: string }) {
  return (
    <span
      role="presentation"
      aria-hidden
      style={{ width: size, height: size, maskImage: 'url(/brand/lion-head.png)', WebkitMaskImage: 'url(/brand/lion-head.png)' }}
      className={cn(MASK_BASE, className)}
    />
  )
}

/**
 * The full walking lion. Wants room — the sign-in page, an empty state, a
 * document header — and is illegible small, which is what `Lion` is for.
 */
export function LionFull({ width = 220, className }: { width?: number; className?: string }) {
  return (
    <span
      role="presentation"
      aria-hidden
      style={{
        width,
        // The engraving trims to 2094 × 1543.
        height: Math.round((width * 1543) / 2094),
        maskImage: 'url(/brand/lion-full.png)',
        WebkitMaskImage: 'url(/brand/lion-full.png)',
      }}
      className={cn(MASK_BASE, className)}
    />
  )
}

/**
 * Lion plus wordmark.
 */
export function Wordmark({ className }: { className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <Lion size={24} />
      <span className="flex flex-col leading-none">
        <span className="font-[family-name:var(--font-heading)] text-[0.9375rem] font-semibold">
          De Leeuw
        </span>
        <span className="mt-[3px] font-[family-name:var(--font-heading)] text-[0.6875rem] text-taupe">
          Studio
        </span>
      </span>
    </span>
  )
}
